/**
 * ApprovalProcessWizard Component
 *
 * Multi-step wizard for creating/editing approval processes.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  Plus,
  Trash2,
  GripVertical,
  AlertCircle,
  Info,
  CheckCircle,
  GitBranch,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  StandardButton,
  StandardInput,
  StandardSelect,
  StandardCheckbox,
  StandardTextarea,
  StandardCard,
  useToast
} from '../../standard';
import * as approvalProcessStorageRouter from '../../../services/approvalProcessStorageRouter';
import type {
  ApprovalProcess,
  ApprovalStep,
  ApprovalCriteria,
  ApprovalProcessAction,
  CreateApprovalProcessPayload
} from '../../../types/approvalProcesses';

// ============================================
// INTERFACES
// ============================================

interface ApprovalProcessWizardProps {
  /** For modal mode */
  isOpen?: boolean;
  onClose?: () => void;
  /** For inline/page mode */
  onSave?: () => void;
  onCancel?: () => void;
  /** Process ID for editing (supports both prop names) */
  editProcessId?: string | null;
  processId?: string | null;
}

interface FormData {
  // Step 1: Basic Info
  name: string;
  description: string;
  objectType: string;
  isActive: boolean;
  order: number;

  // Step 2: Entry Criteria
  entryCriteria: ApprovalCriteria[];
  entryCriteriaLogic: 'AND' | 'OR';

  // Step 3: Approval Steps
  steps: ApprovalStep[];

  // Step 4: Actions
  initialSubmissionActions: string[];
  finalApprovalActions: string[];
  finalRejectionActions: string[];
  recallActions: string[];

  // Step 5: Settings
  allowRecall: boolean;
  recordEditability: 'locked' | 'admin_only' | 'unlocked';
  approverCanEdit: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const WIZARD_STEPS = [
  { id: 1, name: 'Basic Info', description: 'Name and object type' },
  { id: 2, name: 'Entry Criteria', description: 'When to trigger' },
  { id: 3, name: 'Approval Steps', description: 'Who approves' },
  { id: 4, name: 'Actions', description: 'What happens' },
  { id: 5, name: 'Settings', description: 'Configuration' }
];

const OBJECT_TYPE_OPTIONS = [
  { value: '', label: 'Select object type...' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Contract', label: 'Contract' },
  { value: 'CreditApplication', label: 'Credit Application' },
  { value: 'Task', label: 'Task' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Event', label: 'Event' }
];

const FIELD_OPTIONS: Record<string, { value: string; label: string }[]> = {
  Lead: [
    { value: 'status', label: 'Status' },
    { value: 'estimatedValue', label: 'Estimated Value' },
    { value: 'source', label: 'Lead Source' },
    { value: 'rating', label: 'Rating' }
  ],
  Opportunity: [
    { value: 'stage', label: 'Stage' },
    { value: 'amount', label: 'Amount' },
    { value: 'discountPercent', label: 'Discount Percent' },
    { value: 'probability', label: 'Probability' }
  ],
  Case: [
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
    { value: 'type', label: 'Type' },
    { value: 'daysOpen', label: 'Days Open' }
  ],
  Contract: [
    { value: 'status', label: 'Status' },
    { value: 'totalValue', label: 'Total Value' },
    { value: 'term', label: 'Term (Months)' },
    { value: 'type', label: 'Contract Type' }
  ],
  CreditApplication: [
    { value: 'status', label: 'Status' },
    { value: 'applicationType', label: 'Application Type' },
    { value: 'creditScore', label: 'Credit Score' }
  ],
  default: [
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
    { value: 'amount', label: 'Amount' }
  ]
};

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_or_equal', label: 'Greater or Equal' },
  { value: 'less_or_equal', label: 'Less or Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'in_list', label: 'In List' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' }
];

const APPROVER_TYPE_OPTIONS = [
  { value: 'user', label: 'Specific User' },
  { value: 'role', label: 'Role' },
  { value: 'manager', label: 'Record Owner\'s Manager' },
  { value: 'queue', label: 'Queue' },
  { value: 'related_user', label: 'Related User Field' }
];

const ROLE_OPTIONS = [
  { value: 'role_sales_manager', label: 'Sales Manager' },
  { value: 'role_sales_director', label: 'Sales Director' },
  { value: 'role_vp_sales', label: 'VP of Sales' },
  { value: 'role_marketing_manager', label: 'Marketing Manager' },
  { value: 'role_support_manager', label: 'Support Manager' },
  { value: 'role_legal', label: 'Legal Team' },
  { value: 'role_finance', label: 'Finance Team' },
  { value: 'role_executive', label: 'Executive' },
  { value: 'role_credit_analyst', label: 'Credit Analyst' },
  { value: 'role_credit_manager', label: 'Credit Manager' }
];

const USER_OPTIONS = [
  { value: 'user_john', label: 'John Smith' },
  { value: 'user_sarah', label: 'Sarah Johnson' },
  { value: 'user_mike', label: 'Mike Davis' },
  { value: 'user_emily', label: 'Emily Chen' }
];

const RELATED_USER_FIELDS = [
  { value: 'ownerId', label: 'Owner' },
  { value: 'managerId', label: 'Manager' },
  { value: 'createdById', label: 'Created By' },
  { value: 'assignedToId', label: 'Assigned To' }
];

const RECORD_EDITABILITY_OPTIONS = [
  { value: 'locked', label: 'Locked - No one can edit' },
  { value: 'admin_only', label: 'Admin Only - Only admins can edit' },
  { value: 'unlocked', label: 'Unlocked - Anyone can edit' }
];

const ACTION_OPTIONS = [
  { id: 'lock_record', label: 'Lock Record', description: 'Prevent edits while approval is pending' },
  { id: 'unlock_record', label: 'Unlock Record', description: 'Allow edits after approval completes' },
  { id: 'email_approver', label: 'Email Approver', description: 'Send notification to approver' },
  { id: 'email_submitter', label: 'Email Submitter', description: 'Send notification to submitter' },
  { id: 'update_status', label: 'Update Status Field', description: 'Set approval status on record' },
  { id: 'create_task', label: 'Create Follow-up Task', description: 'Create a task for follow-up' }
];

// ============================================
// INITIAL STATE
// ============================================

const getInitialFormData = (): FormData => ({
  name: '',
  description: '',
  objectType: '',
  isActive: true,
  order: 1,
  entryCriteria: [],
  entryCriteriaLogic: 'AND',
  steps: [],
  initialSubmissionActions: ['email_approver'],
  finalApprovalActions: ['unlock_record', 'email_submitter', 'update_status'],
  finalRejectionActions: ['unlock_record', 'email_submitter'],
  recallActions: ['unlock_record'],
  allowRecall: true,
  recordEditability: 'locked',
  approverCanEdit: false
});

// ============================================
// MAIN COMPONENT
// ============================================

export const ApprovalProcessWizard: React.FC<ApprovalProcessWizardProps> = ({
  isOpen = true,
  onClose,
  onSave,
  onCancel,
  editProcessId,
  processId
}) => {
  // Resolve process ID (support both prop names)
  const resolvedProcessId = editProcessId || processId;
  // Resolve close handler (support both modal and inline modes)
  const handleClose = onClose || onCancel || (() => {});

  const { success, error } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (resolvedProcessId) {
      approvalProcessStorageRouter.getProcessAsync(resolvedProcessId).then(process => {
        if (process) {
          setFormData({
            name: process.name,
            description: process.description,
            objectType: process.objectType,
            isActive: process.isActive,
            order: process.order,
            entryCriteria: process.entryCriteria,
            entryCriteriaLogic: process.entryCriteriaLogic,
            steps: process.steps,
            initialSubmissionActions: process.initialSubmissionActions.map(a => a.id),
            finalApprovalActions: process.finalApprovalActions.map(a => a.id),
            finalRejectionActions: process.finalRejectionActions.map(a => a.id),
            recallActions: process.recallActions.map(a => a.id),
            allowRecall: process.allowRecall,
            recordEditability: process.recordEditability,
            approverCanEdit: process.approverCanEdit
          });
        }
      });
    } else {
      setFormData(getInitialFormData());
      setCurrentStep(1);
    }
  }, [resolvedProcessId, isOpen]);

  // Field change handler
  const handleChange = (field: keyof FormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          newErrors.name = 'Process name is required';
        }
        if (!formData.objectType) {
          newErrors.objectType = 'Object type is required';
        }
        break;
      case 3:
        if (formData.steps.length === 0) {
          newErrors.steps = 'At least one approval step is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, WIZARD_STEPS.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Criteria management
  const addCriteria = () => {
    const newCriteria: ApprovalCriteria = {
      id: `criteria_${Date.now()}`,
      field: '',
      operator: 'equals',
      value: '',
      logic: formData.entryCriteria.length > 0 ? 'AND' : undefined
    };
    handleChange('entryCriteria', [...formData.entryCriteria, newCriteria]);
  };

  const updateCriteria = (index: number, updates: Partial<ApprovalCriteria>) => {
    const updated = [...formData.entryCriteria];
    updated[index] = { ...updated[index], ...updates };
    handleChange('entryCriteria', updated);
  };

  const removeCriteria = (index: number) => {
    handleChange('entryCriteria', formData.entryCriteria.filter((_, i) => i !== index));
  };

  // Step management
  const addStep = () => {
    const newStep: ApprovalStep = {
      id: `step_${Date.now()}`,
      stepNumber: formData.steps.length + 1,
      name: `Step ${formData.steps.length + 1}`,
      approverType: 'manager',
      unanimousApproval: false,
      allowDelegation: true
    };
    handleChange('steps', [...formData.steps, newStep]);
  };

  const updateStep = (index: number, updates: Partial<ApprovalStep>) => {
    const updated = [...formData.steps];
    updated[index] = { ...updated[index], ...updates };
    handleChange('steps', updated);
  };

  const removeStep = (index: number) => {
    const updated = formData.steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, stepNumber: i + 1 }));
    handleChange('steps', updated);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.steps.length) return;

    const updated = [...formData.steps];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((step, i) => (step.stepNumber = i + 1));
    handleChange('steps', updated);
  };

  // Action toggle
  const toggleAction = (
    field: 'initialSubmissionActions' | 'finalApprovalActions' | 'finalRejectionActions' | 'recallActions',
    actionId: string
  ) => {
    const current = formData[field];
    const updated = current.includes(actionId)
      ? current.filter(id => id !== actionId)
      : [...current, actionId];
    handleChange(field, updated);
  };

  // Save process
  const handleSave = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      // Convert action IDs to ApprovalProcessAction objects
      const toActions = (ids: string[]): ApprovalProcessAction[] =>
        ids.map(id => ({
          id,
          type: 'field_update' as const,
          name: ACTION_OPTIONS.find(a => a.id === id)?.label || id,
          config: {}
        }));

      const payload: CreateApprovalProcessPayload = {
        name: formData.name,
        description: formData.description,
        objectType: formData.objectType,
        isActive: formData.isActive,
        order: formData.order,
        entryCriteria: formData.entryCriteria,
        entryCriteriaLogic: formData.entryCriteriaLogic,
        steps: formData.steps.map(({ id, ...rest }) => rest),
        initialSubmissionActions: toActions(formData.initialSubmissionActions),
        finalApprovalActions: toActions(formData.finalApprovalActions),
        finalRejectionActions: toActions(formData.finalRejectionActions),
        recallActions: toActions(formData.recallActions),
        allowRecall: formData.allowRecall,
        recordEditability: formData.recordEditability,
        approverCanEdit: formData.approverCanEdit
      };

      if (resolvedProcessId) {
        await approvalProcessStorageRouter.updateProcessAsync({
          id: resolvedProcessId,
          ...payload,
          steps: formData.steps
        });
        success('Updated', 'Approval process updated successfully');
      } else {
        await approvalProcessStorageRouter.createProcessAsync(payload);
        success('Created', 'Approval process created successfully');
      }

      onSave?.();
      handleClose();
    } catch (err) {
      error('Error', 'Failed to save approval process');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Get field options for current object type
  const fieldOptions = FIELD_OPTIONS[formData.objectType] || FIELD_OPTIONS.default;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 rounded-t-xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <GitBranch className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {resolvedProcessId ? 'Edit Approval Process' : 'Create Approval Process'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Step {currentStep} of {WIZARD_STEPS.length}: {WIZARD_STEPS[currentStep - 1].name}
              </p>
            </div>
          </div>
          <StandardButton variant="ghost" onClick={handleClose} size="sm">
            <X size={20} />
          </StandardButton>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold transition-all ${
                      currentStep > step.id
                        ? 'bg-green-500 text-white shadow-md shadow-green-200 dark:shadow-green-900/30'
                        : currentStep === step.id
                        ? 'bg-purple-600 text-white shadow-md shadow-purple-200 dark:shadow-purple-900/30 ring-4 ring-purple-100 dark:ring-purple-900/30'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {currentStep > step.id ? <Check size={16} /> : step.id}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p
                      className={`text-sm font-medium ${
                        currentStep >= step.id
                          ? 'text-gray-900 dark:text-gray-100'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {step.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < WIZARD_STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 transition-colors ${
                      currentStep > step.id
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <StandardInput
                label="Process Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Opportunity Discount Approval"
                error={errors.name}
                required
              />

              <StandardTextarea
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe when and how this approval process should be used..."
                rows={3}
              />

              <StandardSelect
                label="Object Type"
                value={formData.objectType}
                onChange={(e) => handleChange('objectType', e.target.value)}
                options={OBJECT_TYPE_OPTIONS}
                error={errors.objectType}
                required
              />

              <div className="flex gap-6">
                <StandardCheckbox
                  checked={formData.isActive}
                  onChange={(checked) => handleChange('isActive', checked)}
                  label="Active"
                  helpText="Enable this process for submissions"
                />
              </div>
            </div>
          )}

          {/* Step 2: Entry Criteria */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Entry Criteria
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Define when this approval process should be triggered
                  </p>
                </div>
                <StandardButton
                  variant="secondary"
                  size="sm"
                  onClick={addCriteria}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Add Criteria
                </StandardButton>
              </div>

              {formData.entryCriteria.length > 1 && (
                <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Match:</span>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formData.entryCriteriaLogic === 'AND'}
                      onChange={() => handleChange('entryCriteriaLogic', 'AND')}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">All criteria (AND)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formData.entryCriteriaLogic === 'OR'}
                      onChange={() => handleChange('entryCriteriaLogic', 'OR')}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Any criteria (OR)</span>
                  </label>
                </div>
              )}

              {formData.entryCriteria.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No criteria added. Process will apply to all records of this type.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.entryCriteria.map((criteria, index) => (
                    <div
                      key={criteria.id}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                    >
                      <StandardSelect
                        value={criteria.field}
                        onChange={(e) => updateCriteria(index, { field: e.target.value })}
                        options={[{ value: '', label: 'Select field...' }, ...fieldOptions]}
                        className="flex-1"
                      />
                      <StandardSelect
                        value={criteria.operator}
                        onChange={(e) => updateCriteria(index, { operator: e.target.value as ApprovalCriteria['operator'] })}
                        options={OPERATOR_OPTIONS}
                        className="w-40"
                      />
                      <StandardInput
                        value={String(criteria.value)}
                        onChange={(e) => updateCriteria(index, { value: e.target.value })}
                        placeholder="Value"
                        className="flex-1"
                      />
                      <StandardButton
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCriteria(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </StandardButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Approval Steps */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Approval Steps
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Configure the approval chain
                  </p>
                </div>
                <StandardButton
                  variant="secondary"
                  size="sm"
                  onClick={addStep}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Add Step
                </StandardButton>
              </div>

              {errors.steps && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {errors.steps}
                </div>
              )}

              {formData.steps.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Add at least one approval step
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.steps.map((step, index) => (
                    <div
                      key={step.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                        <span className="flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-medium">
                          {step.stepNumber}
                        </span>
                        <StandardInput
                          value={step.name}
                          onChange={(e) => updateStep(index, { name: e.target.value })}
                          placeholder="Step name"
                          className="flex-1"
                        />
                        <div className="flex gap-1">
                          <StandardButton
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStep(index, 'up')}
                            disabled={index === 0}
                            title="Move up"
                          >
                            <ArrowUp size={14} />
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStep(index, 'down')}
                            disabled={index === formData.steps.length - 1}
                            title="Move down"
                          >
                            <ArrowDown size={14} />
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStep(index)}
                            title="Remove step"
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </StandardButton>
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <StandardSelect
                            label="Approver Type"
                            value={step.approverType}
                            onChange={(e) => updateStep(index, { approverType: e.target.value as ApprovalStep['approverType'] })}
                            options={APPROVER_TYPE_OPTIONS}
                          />

                          {step.approverType === 'user' && (
                            <StandardSelect
                              label="Select User"
                              value={step.approverId || ''}
                              onChange={(e) => updateStep(index, { approverId: e.target.value })}
                              options={[{ value: '', label: 'Select user...' }, ...USER_OPTIONS]}
                            />
                          )}

                          {step.approverType === 'role' && (
                            <StandardSelect
                              label="Select Role"
                              value={step.approverId || ''}
                              onChange={(e) => updateStep(index, { approverId: e.target.value })}
                              options={[{ value: '', label: 'Select role...' }, ...ROLE_OPTIONS]}
                            />
                          )}

                          {step.approverType === 'related_user' && (
                            <StandardSelect
                              label="Related User Field"
                              value={step.approverField || ''}
                              onChange={(e) => updateStep(index, { approverField: e.target.value })}
                              options={[{ value: '', label: 'Select field...' }, ...RELATED_USER_FIELDS]}
                            />
                          )}
                        </div>

                        <div className="flex flex-wrap gap-6">
                          <StandardCheckbox
                            checked={step.allowDelegation}
                            onChange={(checked) => updateStep(index, { allowDelegation: checked })}
                            label="Allow delegation"
                          />
                          <StandardCheckbox
                            checked={step.unanimousApproval}
                            onChange={(checked) => updateStep(index, { unanimousApproval: checked })}
                            label="Unanimous approval required"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <StandardInput
                            type="number"
                            label="Auto-escalate after (days)"
                            value={step.autoEscalateAfterDays || ''}
                            onChange={(e) => updateStep(index, { autoEscalateAfterDays: e.target.value ? parseInt(e.target.value) : undefined })}
                            placeholder="Leave empty to disable"
                          />
                          {step.autoEscalateAfterDays && (
                            <StandardSelect
                              label="Escalate To"
                              value={step.escalateTo || ''}
                              onChange={(e) => updateStep(index, { escalateTo: e.target.value })}
                              options={[{ value: '', label: 'Select...' }, ...ROLE_OPTIONS]}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Actions */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <ActionGroup
                title="Initial Submission Actions"
                description="Actions to perform when a record is submitted for approval"
                selected={formData.initialSubmissionActions}
                onToggle={(id) => toggleAction('initialSubmissionActions', id)}
              />

              <ActionGroup
                title="Final Approval Actions"
                description="Actions to perform when approval is granted"
                selected={formData.finalApprovalActions}
                onToggle={(id) => toggleAction('finalApprovalActions', id)}
              />

              <ActionGroup
                title="Final Rejection Actions"
                description="Actions to perform when approval is rejected"
                selected={formData.finalRejectionActions}
                onToggle={(id) => toggleAction('finalRejectionActions', id)}
              />

              <ActionGroup
                title="Recall Actions"
                description="Actions to perform when submitter recalls the request"
                selected={formData.recallActions}
                onToggle={(id) => toggleAction('recallActions', id)}
              />
            </div>
          )}

          {/* Step 5: Settings & Review */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Additional Settings
                </h3>
                <div className="space-y-4">
                  <StandardCheckbox
                    checked={formData.allowRecall}
                    onChange={(checked) => handleChange('allowRecall', checked)}
                    label="Allow submitter to recall"
                    helpText="Submitter can withdraw the approval request while pending"
                  />

                  <StandardSelect
                    label="Record Editability"
                    value={formData.recordEditability}
                    onChange={(e) => handleChange('recordEditability', e.target.value)}
                    options={RECORD_EDITABILITY_OPTIONS}
                    helpText="Who can edit the record while approval is pending"
                  />

                  <StandardCheckbox
                    checked={formData.approverCanEdit}
                    onChange={(checked) => handleChange('approverCanEdit', checked)}
                    label="Approvers can edit record"
                    helpText="Allow approvers to make changes during review"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Review Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Process Name</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formData.name || '-'}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Object Type</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formData.objectType || '-'}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Entry Criteria</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {formData.entryCriteria.length} condition(s)
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Approval Steps</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formData.steps.length} step(s)</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Status</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Allow Recall</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {formData.allowRecall ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
          <StandardButton variant="ghost" onClick={handleClose}>
            Cancel
          </StandardButton>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <StandardButton
                variant="secondary"
                onClick={handlePrevious}
                leftIcon={<ChevronLeft size={16} />}
              >
                Previous
              </StandardButton>
            )}

            {currentStep < WIZARD_STEPS.length ? (
              <StandardButton
                variant="primary"
                onClick={handleNext}
                rightIcon={<ChevronRight size={16} />}
              >
                Continue
              </StandardButton>
            ) : (
              <StandardButton
                variant="primary"
                onClick={handleSave}
                disabled={isSubmitting}
                leftIcon={isSubmitting ? undefined : <CheckCircle size={16} />}
              >
                {isSubmitting ? 'Saving...' : resolvedProcessId ? 'Save Changes' : 'Create Process'}
              </StandardButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ACTION GROUP COMPONENT
// ============================================

const ActionGroup: React.FC<{
  title: string;
  description: string;
  selected: string[];
  onToggle: (id: string) => void;
}> = ({ title, description, selected, onToggle }) => (
  <div>
    <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{description}</p>
    <div className="space-y-2">
      {ACTION_OPTIONS.map(action => (
        <label
          key={action.id}
          className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900"
        >
          <input
            type="checkbox"
            checked={selected.includes(action.id)}
            onChange={() => onToggle(action.id)}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{action.label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{action.description}</p>
          </div>
        </label>
      ))}
    </div>
  </div>
);

export default ApprovalProcessWizard;
